import { EVENT_TYPE } from "@/api/analytics/api"
import { useCreateGameEvent } from "@/api/analytics/hooks"
import useGetComicsDetail from "@/api/hooks/useComicsDetail"
import { finishGamePlayer } from "@/api/playerGame"
import GameResultPage from "@/components/pages/CartoonDetailPage/GameResultPage"
import GameContent from "@/components/pages/CartoonDetailPage/GameSection/GameContent"
import Container from "@/components/shared/Container"
import RoundedButton from "@/components/shared/RoundedButton"
import { ROUTES } from "@/conf"
import { useQueryClient } from "@tanstack/react-query"
import Image from "next/image"
import { useParams, useRouter } from "next/navigation"
import { useEffect, useState } from "react"

const GameSection: React.FC = () => {
  const router = useRouter()
  const params = useParams()
  const comicsSlug = params.slug

  const { comicsDetail } = useGetComicsDetail(String(comicsSlug))
  const { mutateAsync: createAnalytics } = useCreateGameEvent()

  const [comicsFinished, setIsComicsFinished] = useState<boolean>(false)
  const [gainedGroshaks, setGainedGroshaks] = useState<number>(0)

  const [latestQuestionShown, setLatestQuestionShown] = useState<{
    questionId: string
    questionPosition: number
    time: number
  } | null>(null)
  const [startTime, setStartTime] = useState<number>(0)
  const [numberOfTries, setNumberOfTries] = useState(0)

  const queryClient = useQueryClient()

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      console.log(event.data)

      if (event.data.type === "COMIC_STARTED") {
        setStartTime(Date.now())
        createAnalytics({
          comicId: comicsDetail?.comicId || "",
          time: Date.now(),
          event: EVENT_TYPE.COMIC_START,
        })
      }

      if (event.data.type === "QUESTION_SHOWN") {
        setLatestQuestionShown({
          questionId: event?.data?.questionId,
          questionPosition: event?.data?.questionPosition,
          time: Date.now(),
        })

        createAnalytics({
          comicId: comicsDetail?.comicId || "",
          questionPosition: event?.data?.questionPosition,
          questionId: event?.data?.questionId,
          time: Date.now(),
          isTournament: false,
          event: EVENT_TYPE.QUESTION_SHOWN,
        })
      }

      if (event.data.type === "QUESTION_ANSWER") {
        setNumberOfTries((prev) => prev + 1)
        if (event.data.isCorrect) {
          setNumberOfTries(0)

          if (!comicsDetail?.game.claimed) {
            createAnalytics({
              comicId: comicsDetail?.comicId || "",
              questionPosition: latestQuestionShown?.questionPosition || 0,
              questionId: latestQuestionShown?.questionId || "",
              isTournament: false,
              numberOfTries: numberOfTries + 1,
              questionTime: Date.now() - (latestQuestionShown?.time || 0),
              time: Date.now(),
              event: EVENT_TYPE.QUESTION_ANSWER,
            })
          }

          createAnalytics({
            comicId: comicsDetail?.comicId || "",
            questionPosition: latestQuestionShown?.questionPosition || 0,
            questionId: latestQuestionShown?.questionId || "",
            isTournament: false,
            time: Date.now(),
            questionTime: Date.now() - (latestQuestionShown?.time || 0),
            event: EVENT_TYPE.QUESTION_DURATION,
          })
        }
      }

      if (event.data.type === "COMIC_FINISHED") {
        const gameId = comicsDetail?.game?.gameId
        const comicsClaimed = comicsDetail?.game.claimed

        createAnalytics({
          comicId: comicsDetail?.comicId || "",
          comicTime: Date.now() - startTime,
          isTournament: false,
          time: Date.now(),
          event: EVENT_TYPE.COMIC_DURATION,
        })

        if (gameId) {
          if (!comicsClaimed) {
            finishGamePlayer({ gainedGroshak: event?.data?.coinsGained }, gameId)
              .then(async (r) => {
                await createAnalytics({
                  comicId: comicsDetail?.comicId || "",
                  time: Date.now(),
                  event: EVENT_TYPE.GAME_CLAIMED,
                })

                setIsComicsFinished(true)
                setGainedGroshaks(event?.data?.coinsGained)
                queryClient.invalidateQueries(["playerData"])
                queryClient.invalidateQueries(["comicsDetail"])
              })
              .catch((e) => {
                console.error(e)
              })
          } else {
            router.push(`${ROUTES.cartoonDetail}/${params.slug}`)
          }
        }
      }
    }
    window.addEventListener("message", handleMessage)
    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [
    latestQuestionShown,
    comicsDetail?.comicId,
    comicsDetail?.game.claimed,
    comicsDetail?.game?.gameId,
    createAnalytics,
    params.slug,
    queryClient,
    router,
    startTime,
    numberOfTries,
  ])

  if (comicsFinished) {
    return (
      <GameResultPage
        gainedGroshaks={gainedGroshaks}
        comicsBackgroundImageUrl={comicsDetail?.backgroundImageUrl || ""}
        comicsName={comicsDetail?.name || ""}
      />
    )
  }

  return (
    <div className="bg-[url('/assets/images/game_bg.png')] bg-cover	">
      <Container className="relative flex h-[calc(100vh-80px)] justify-center">
        <RoundedButton
          onClick={() => router.back()}
          className="absolute left-6 top-8 z-20"
          icon={<Image src="/assets/images/arrow_left.svg" width={32} height={32} alt="" />}
        />
        <div className="relative h-full w-96 max-w-md">
          <GameContent
            gameSrc={
              comicsDetail?.game.claimed
                ? `${comicsDetail?.game?.htmlFileUrl}?hidePlus&hideCoins`
                : `${comicsDetail?.game?.htmlFileUrl}?hidePlus`
            }
          />
        </div>
      </Container>
    </div>
  )
}

export default GameSection
